import React, { useState } from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';

export interface TreemapNode {
    name: string;
    value: number;
    children?: TreemapNode[];
    color?: string;
}

export interface TreemapBlockData {
    title?: string;
    data: TreemapNode[];
    valueLabel?: string;
}

interface TreemapBlockProps {
    data: TreemapBlockData;
    /** Callback when a node is clicked — used for drill-down */
    onNodeClick?: (node: TreemapNode) => void;
}

const COLORS = [
    '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b',
    '#ef4444', '#06b6d4', '#ec4899', '#6366f1',
    '#14b8a6', '#f97316', '#84cc16', '#a855f7',
];

// Custom content renderer for Treemap cells
const CustomContent: React.FC<any> = (props) => {
    const { x, y, width, height, name, value, depth, index, onNodeClick } = props;

    if (width < 4 || height < 4) return null;

    const colorIdx = (depth === 1 ? index : (props.root?.index ?? index)) % COLORS.length;
    const bgColor = COLORS[colorIdx];
    const showLabel = width > 40 && height > 24;
    const showValue = width > 60 && height > 36;

    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                rx={3}
                ry={3}
                style={{
                    fill: bgColor,
                    stroke: '#ffffff',
                    strokeWidth: depth === 1 ? 2 : 1,
                    opacity: depth === 1 ? 0.9 : 0.75,
                    cursor: onNodeClick ? 'pointer' : 'default',
                }}
                onClick={() => onNodeClick?.({ name, value })}
            />
            {showLabel && (
                <text
                    x={x + width / 2}
                    y={y + height / 2 - (showValue ? 6 : 0)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={Math.min(12, width / 8)}
                    fontWeight={600}
                >
                    {name.length > width / 7 ? name.slice(0, Math.floor(width / 7)) + '…' : name}
                </text>
            )}
            {showValue && (
                <text
                    x={x + width / 2}
                    y={y + height / 2 + 10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="rgba(255,255,255,0.7)"
                    fontSize={10}
                >
                    {typeof value === 'number' ? value.toLocaleString('nb-NO') : value}
                </text>
            )}
        </g>
    );
};

export const TreemapBlock: React.FC<TreemapBlockProps> = ({ data, onNodeClick }) => {
    const [activeNode, setActiveNode] = useState<string | null>(null);

    if (!data.data || data.data.length === 0) {
        return <p className="text-sm text-gray-500 py-2">Ingen data for treemap</p>;
    }

    // Recharts Treemap expects { name, children: [{ name, size }] } structure
    // but also accepts flat { name, value } arrays.
    // We need to wrap in a root node for nested data.
    const treemapData = data.data.some(d => d.children && d.children.length > 0)
        ? data.data.map(node => ({
            name: node.name,
            children: (node.children || []).map(child => ({
                name: child.name,
                size: child.value,
            })),
        }))
        : data.data.map(node => ({
            name: node.name,
            size: node.value,
        }));

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;
        const item = payload[0]?.payload;
        if (!item) return null;

        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
                <p className="font-semibold text-gray-800">{item.name}</p>
                <p className="text-gray-600">
                    {(item.size ?? item.value ?? 0).toLocaleString('nb-NO')}
                    {data.valueLabel ? ` ${data.valueLabel}` : ''}
                </p>
            </div>
        );
    };

    return (
        <div className="mt-2">
            {data.title && (
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{data.title}</h4>
            )}
            <ResponsiveContainer width="100%" height={300}>
                <Treemap
                    data={treemapData}
                    dataKey="size"
                    stroke="#fff"
                    content={<CustomContent onNodeClick={onNodeClick} />}
                >
                    <Tooltip content={<CustomTooltip />} />
                </Treemap>
            </ResponsiveContainer>

            {/* Legend for top-level categories */}
            {data.data.length <= 12 && (
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                    {data.data.map((node, idx) => (
                        <div
                            key={idx}
                            className="flex items-center gap-1.5 text-[10px] text-gray-600"
                        >
                            <div
                                className="w-2.5 h-2.5 rounded-sm"
                                style={{ background: COLORS[idx % COLORS.length] }}
                            />
                            {node.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
